import React, { useState, useMemo } from 'react';
import {
  Download, Loader2, Sparkles, Edit3, MessageSquare, Image, RotateCcw,
  Undo2, CheckCircle2, Settings, ChevronLeft
} from 'lucide-react';
import SectionEditor from './SectionEditor';
import ChatPanel from './ChatPanel';

// Parse markdown table rows into cells
const parseTableRow = (line) => {
  return line.split('|').slice(1, -1).map(cell => cell.trim());
};

const isTableSeparator = (line) => /^\|[\s:]*[-]+[\s:]*/.test(line.trim());

// Render markdown content as simple HTML for reading mode
const renderContent = (content) => {
  if (!content) return '';
  // If already HTML
  if (content.trim().startsWith('<')) return content;

  let html = '';
  let inList = null; // 'ul' | 'ol'
  let inCodeBlock = false;
  const lines = content.split('\n');
  let i = 0;

  const closeList = () => {
    if (inList) { html += `</${inList}>`; inList = null; }
  };

  while (i < lines.length) {
    const stripped = lines[i].trim();

    if (stripped.startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      i++;
      continue;
    }
    if (inCodeBlock) { i++; continue; }
    if (!stripped) {
      closeList();
      i++;
      continue;
    }

    // Detect markdown table: line starts with | and contains at least one more |
    if (stripped.startsWith('|') && stripped.indexOf('|', 1) > 0) {
      closeList();
      // Collect all consecutive table rows
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().indexOf('|', 1) > 0) {
        tableLines.push(lines[i].trim());
        i++;
      }
      // Build HTML table
      if (tableLines.length > 0) {
        html += '<table class="w-full text-sm border-collapse my-3">';
        let headerDone = false;
        for (let r = 0; r < tableLines.length; r++) {
          if (isTableSeparator(tableLines[r])) {
            headerDone = true;
            continue;
          }
          const cells = parseTableRow(tableLines[r]);
          const isHeader = !headerDone && r === 0;
          const tag = isHeader ? 'th' : 'td';
          const cellClass = isHeader
            ? 'px-3 py-1.5 text-left font-semibold border-b-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
            : 'px-3 py-1.5 text-left border-b border-gray-200 dark:border-gray-700';
          html += '<tr>';
          for (const cell of cells) {
            html += `<${tag} class="${cellClass}">${formatInline(cell)}</${tag}>`;
          }
          html += '</tr>';
          if (isHeader) headerDone = true;
        }
        html += '</table>';
      }
      continue;
    }

    if (stripped.startsWith('####')) {
      closeList();
      html += `<h4 class="font-semibold text-sm mt-3 mb-1">${formatInline(stripped.replace(/^#+\s*/, ''))}</h4>`;
    } else if (stripped.startsWith('###')) {
      closeList();
      html += `<h3 class="font-semibold mt-4 mb-1">${formatInline(stripped.replace(/^#+\s*/, ''))}</h3>`;
    } else if (stripped.startsWith('##') || stripped.startsWith('# ')) {
      closeList();
      html += `<h2 class="font-bold text-lg mt-5 mb-2">${formatInline(stripped.replace(/^#+\s*/, ''))}</h2>`;
    } else if (/^[-•*]\s/.test(stripped)) {
      if (inList !== 'ul') {
        if (inList) html += `</${inList}>`;
        html += '<ul class="list-disc pl-5 space-y-0.5">';
        inList = 'ul';
      }
      html += `<li class="text-sm">${formatInline(stripped.replace(/^[-•*]\s*/, ''))}</li>`;
    } else if (/^\d+\.\s/.test(stripped)) {
      if (inList !== 'ol') {
        if (inList) html += `</${inList}>`;
        html += '<ol class="list-decimal pl-5 space-y-0.5">';
        inList = 'ol';
      }
      html += `<li class="text-sm">${formatInline(stripped.replace(/^\d+\.\s*/, ''))}</li>`;
    } else if (stripped.startsWith('>')) {
      closeList();
      html += `<blockquote class="border-l-3 pl-3 italic opacity-80 my-2 text-sm">${formatInline(stripped.replace(/^>\s*/, ''))}</blockquote>`;
    } else if (/^[-*_]{3,}\s*$/.test(stripped)) {
      closeList();
      html += '<hr class="my-4 border-gray-200 dark:border-gray-700" />';
    } else {
      closeList();
      html += `<p class="text-sm my-1.5">${formatInline(stripped)}</p>`;
    }
    i++;
  }
  closeList();
  return html;
};

// Bold / italic inline formatting
const formatInline = (text) => {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
};

const DocumentWorkspace = ({
  isDark, topic, sections, images, isGenerating, generationStatus,
  sectionHistory, chatPanelSection, chatHistories, isBuilding,
  onUpdateSection, onRegenerateSection, onUndoRegenerate,
  onRegenerateImage, onChatWithSection, onOpenChat, onCloseChat,
  onBuild, onBackToSetup
}) => {
  const [editingSection, setEditingSection] = useState(null);
  const [regenInput, setRegenInput] = useState({});
  const [loadingSection, setLoadingSection] = useState(null);
  const [hoveredSection, setHoveredSection] = useState(null);

  const completedCount = sections.length;
  const totalCount = generationStatus?.total_sections || sections.length;
  const currentTitle = generationStatus?.current_section_title || '';
  const isComplete = !isGenerating && sections.length > 0;

  const handleRegenerate = async (sectionId) => {
    setLoadingSection(sectionId);
    await onRegenerateSection(sectionId, regenInput[sectionId] || null);
    setRegenInput(prev => ({ ...prev, [sectionId]: '' }));
    setLoadingSection(null);
  };

  const handleSaveEdit = (sectionId, content) => {
    onUpdateSection(sectionId, content);
    setEditingSection(null);
  };

  const activeChatSection = chatPanelSection
    ? sections.find(s => s.id === chatPanelSection)
    : null;

  return (
    <div className="flex h-full">
      {/* ==================== Main Content ==================== */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto px-6 py-6">

          {/* Generation Progress */}
          {isGenerating && (
            <div className={`mb-6 p-4 rounded-xl border ${isDark ? 'border-blue-800 bg-blue-900/20' : 'border-blue-200 bg-blue-50'}`}>
              <div className="flex items-center gap-3 mb-2">
                <Loader2 size={18} className="animate-spin text-blue-500" />
                <div className="flex-1">
                  <div className="text-sm font-medium">
                    Generating content... Section {generationStatus?.current_section || 0} of {totalCount}
                  </div>
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                    {currentTitle && `Currently writing: ${currentTitle}`}
                  </div>
                </div>
              </div>
              <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-gray-700' : 'bg-blue-100'}`}>
                <div
                  className="h-full rounded-full bg-blue-500 transition-all duration-500"
                  style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Completion Banner */}
          {isComplete && !isGenerating && (
            <div className={`mb-6 p-3 rounded-xl flex items-center gap-3 ${
              isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
            }`}>
              <CheckCircle2 size={18} className="text-green-500 shrink-0" />
              <div className="flex-1">
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  {sections.length} sections ready
                </span>
                <span className={`text-xs ml-2 ${isDark ? 'text-green-500' : 'text-green-600'}`}>
                  Click any section to edit, or use the toolbar to chat with AI
                </span>
              </div>
              <button
                onClick={onBuild}
                disabled={isBuilding}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-500 text-white text-sm font-medium hover:bg-green-600 transition-colors disabled:opacity-50 shrink-0"
              >
                {isBuilding ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Download DOCX
              </button>
            </div>
          )}

          {/* Back to setup */}
          {!isGenerating && (
            <button
              onClick={onBackToSetup}
              className={`flex items-center gap-1 text-xs mb-4 transition-colors ${
                isDark ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <ChevronLeft size={12} /> Back to setup
            </button>
          )}

          {/* ==================== Document Content ==================== */}
          <div className="space-y-1">
            {sections.map((section, idx) => {
              const isEditing = editingSection === section.id;
              const isLoading = loadingSection === section.id;
              const isHovered = hoveredSection === section.id;
              const hasUndo = !!sectionHistory[section.id];
              const sectionImage = images[section.id];

              return (
                <div
                  key={section.id}
                  className="relative group"
                  onMouseEnter={() => setHoveredSection(section.id)}
                  onMouseLeave={() => setHoveredSection(null)}
                >
                  {/* Page break indicator (between sections, not before first) */}
                  {idx > 0 && (
                    <div className={`border-t my-6 ${isDark ? 'border-gray-700' : 'border-gray-200'}`} />
                  )}

                  {/* Section Heading */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h2 className={`font-bold ${
                      section.level <= 1 ? 'text-xl' : 'text-lg'
                    }`}>
                      {section.title}
                    </h2>

                    {/* Hover Toolbar */}
                    {!isGenerating && !isEditing && (
                      <div className={`flex items-center gap-0.5 shrink-0 transition-opacity ${
                        isHovered ? 'opacity-100' : 'opacity-0'
                      }`}>
                        <button
                          onClick={() => setEditingSection(section.id)}
                          title="Edit section"
                          className={`p-1.5 rounded-lg text-xs transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => onOpenChat(section.id)}
                          title="Chat with AI"
                          className={`p-1.5 rounded-lg text-xs transition-colors ${
                            chatPanelSection === section.id
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                              : isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                          }`}
                        >
                          <MessageSquare size={14} />
                        </button>
                        <button
                          onClick={() => handleRegenerate(section.id)}
                          disabled={isLoading}
                          title="Regenerate section"
                          className={`p-1.5 rounded-lg text-xs transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                        >
                          {isLoading ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                        </button>
                        <button
                          onClick={() => onRegenerateImage(section.id)}
                          title="Generate image"
                          className={`p-1.5 rounded-lg text-xs transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                        >
                          <Image size={14} />
                        </button>
                        {hasUndo && (
                          <button
                            onClick={() => onUndoRegenerate(section.id)}
                            title="Undo last regeneration"
                            className="p-1.5 rounded-lg text-xs text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                          >
                            <Undo2 size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Section Content */}
                  {isEditing ? (
                    <SectionEditor
                      isDark={isDark}
                      content={section.generated_content}
                      onSave={(content) => handleSaveEdit(section.id, content)}
                      onCancel={() => setEditingSection(null)}
                    />
                  ) : isLoading ? (
                    <div className={`flex items-center gap-2 py-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <Loader2 size={16} className="animate-spin" />
                      <span className="text-sm">Regenerating...</span>
                    </div>
                  ) : section.generated_content ? (
                    <div
                      className={`prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''}`}
                      dangerouslySetInnerHTML={{ __html: renderContent(section.generated_content) }}
                    />
                  ) : (
                    <div className={`py-4 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <Loader2 size={14} className="inline animate-spin mr-2" />
                      Generating content...
                    </div>
                  )}

                  {/* Section Image */}
                  {sectionImage && (
                    <div className="mt-4 flex justify-center">
                      <img
                        src={`data:image/png;base64,${sectionImage}`}
                        alt={`Illustration for ${section.title}`}
                        className="max-w-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
                      />
                    </div>
                  )}

                  {/* Regen with custom instruction (visible on hover) */}
                  {isHovered && !isGenerating && !isEditing && !isLoading && (
                    <div className={`mt-2 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={regenInput[section.id] || ''}
                          onChange={(e) => setRegenInput(prev => ({ ...prev, [section.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRegenerate(section.id); }}
                          placeholder="Regenerate with instructions..."
                          className={`flex-1 px-2 py-1 rounded border text-xs ${
                            isDark
                              ? 'bg-gray-800 border-gray-600 text-gray-300 placeholder-gray-600'
                              : 'bg-gray-50 border-gray-200 text-gray-700 placeholder-gray-400'
                          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom Download Button */}
          {isComplete && sections.length > 3 && (
            <div className="mt-10 mb-6 text-center">
              <button
                onClick={onBuild}
                disabled={isBuilding}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-green-500 text-white font-medium hover:bg-green-600 transition-colors disabled:opacity-50 shadow-sm"
              >
                {isBuilding ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                Download DOCX
              </button>
            </div>
          )}

          {/* Empty state during generation */}
          {sections.length === 0 && isGenerating && (
            <div className="text-center py-16">
              <Loader2 size={32} className="animate-spin text-blue-500 mx-auto mb-4" />
              <p className="text-sm font-medium">Starting content generation...</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Sections will appear here as they're generated
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ==================== Chat Side Panel ==================== */}
      {activeChatSection && (
        <ChatPanel
          isDark={isDark}
          section={activeChatSection}
          chatHistory={chatHistories[activeChatSection.id] || []}
          onSend={onChatWithSection}
          onClose={onCloseChat}
        />
      )}
    </div>
  );
};

export default DocumentWorkspace;
